import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, Trash2, Radio, History } from "lucide-react";
import { adminApi } from "../../../lib/api";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "";

// Server-side reporting of confirmed Discovery Calls. The secret goes in and is
// encrypted; it is checked against GA4 before being kept, and the card reports
// "Working" only once a real booking has actually been sent.
export default function MeasurementProtocolCard({ status, onChange }) {
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [replayed, setReplayed] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  const mp = status?.measurement_protocol || {};
  const state = mp.status || "not configured";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await adminApi.ga4.saveMeasurementSecret(secret);
      setSecret("");
      onChange?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "The secret could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError("");
    setVerifyResult(null);
    try {
      const res = await adminApi.ga4.verifyServerTracking();
      setVerifyResult(res);
      onChange?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "The check could not be run.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminApi.ga4.deleteMeasurementSecret();
      onChange?.();
    } finally {
      setBusy(false);
    }
  };

  const replay = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await adminApi.ga4.replayBookings();
      setReplayed(res);
      onChange?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Those bookings could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="mp-secret-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
          <Radio size={16} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-navy">Server-side booking tracking</h3>
            <span
              data-testid="mp-status-badge"
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                state === "working"
                  ? "bg-emerald-100 text-emerald-700"
                  : state === "verified"
                  ? "bg-sky-100 text-sky-700"
                  : state === "unverified"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {state === "working"
                ? "Working"
                : state === "verified"
                ? "Active, awaiting a booking"
                : state === "unverified"
                ? "Secret saved, not yet proven"
                : "Not configured"}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Sends <code className="text-xs">discovery_call_booked</code> to GA4 when Calendly confirms a call, so a
            booking still counts when the visitor has closed the tab or refused analytics cookies. The page
            suppresses its own event for any booking the server reports, so one booking stays one event.
          </p>
        </div>
      </div>

      {mp.configured && (
        <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-600" data-testid="mp-evidence">
          <p>
            Stream {mp.measurement_id || "—"}
            {mp.secret_saved_at ? ` · secret saved ${when(mp.secret_saved_at)}` : ""}
          </p>
          <p className="mt-1">
            {mp.verified_at ? `Verified against GA4 ${when(mp.verified_at)}. ` : ""}
            {mp.bookings_reported
              ? `${mp.bookings_reported} confirmed booking${mp.bookings_reported === 1 ? "" : "s"} reported to GA4 · last ${when(mp.last_booking_reported)}`
              : "No confirmed booking has been reported yet — the next genuine booking will prove it end to end."}
          </p>
        </div>
      )}

      {mp.configured && !mp.verified && (
        <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-900 leading-relaxed" data-testid="mp-verify-notice">
          <p>
            GA4 accepts any secret without complaining — right, wrong or invented — so saving it proves nothing on
            its own. Until a test event has been seen arriving in the property, the browser carries on reporting
            bookings and the server stays quiet, so no booking is lost and none is counted twice.
          </p>
          <button
            type="button"
            onClick={verify}
            disabled={busy}
            data-testid="mp-verify"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-[11px] font-bold text-white duration-200 [transition-property:background-color,opacity] hover:bg-navy/90 disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
            {busy ? "Sending a test event and watching GA4…" : "Send a test event and check GA4"}
          </button>
          {mp.can_verify === false && (
            <p className="mt-2 text-[11px]">
              This check reads the property back, so it needs the service account key uploaded above. Without it you
              can still watch GA4 → Reports → Realtime yourself.
            </p>
          )}
        </div>
      )}

      {verifyResult && (
        <div className="mt-3" data-testid="mp-verify-result">
          <p className={`flex items-start gap-2 text-xs ${verifyResult.verified ? "text-emerald-600" : "text-amber-700"}`}>
            {verifyResult.verified ? (
              <CheckCircle2 size={14} className="shrink-0 mt-px" />
            ) : (
              <AlertCircle size={14} className="shrink-0 mt-px" />
            )}
            <span>{verifyResult.detail}</span>
          </p>
          {(verifyResult.steps || []).length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="mp-verify-steps">
              {verifyResult.steps.map((s) => (
                <li key={s.step} className="flex items-start gap-2 text-[11px] text-slate-600">
                  {s.ok ? (
                    <CheckCircle2 size={12} className="shrink-0 mt-0.5 text-emerald-500" />
                  ) : (
                    <AlertCircle size={12} className="shrink-0 mt-0.5 text-amber-500" />
                  )}
                  <span>
                    <span className="font-semibold text-navy">{s.step}:</span> {s.detail}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mp.configured ? null : (
        <form onSubmit={save} className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[260px]">
            <span className="text-xs font-semibold text-navy">Measurement Protocol API secret</span>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="Paste the secret value"
              data-testid="mp-secret-input"
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy outline-none duration-200 [transition-property:border-color] focus:border-orange"
            />
            <span className="text-[11px] text-slate-400 mt-1 block">
              GA4 Admin → Data Streams → your web stream → Measurement Protocol API secrets. Copy the secret
              value, not its nickname.
            </span>
          </label>
          <button
            type="submit"
            disabled={busy || !secret}
            data-testid="mp-secret-save"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white duration-200 [transition-property:background-color,opacity] hover:bg-navy/90 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {busy ? "Checking with GA4…" : mp.configured ? "Replace secret" : "Activate tracking"}
          </button>
        </form>
      )}

      {mp.secret_source === "saved" && (
        <div className="mt-4 flex flex-wrap items-center gap-4">
          {mp.verified && (
          <button
            type="button"
            onClick={replay}
            disabled={busy}
            data-testid="mp-replay"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy duration-200 [transition-property:color] disabled:opacity-50"
          >
            <History size={12} /> Send bookings missed while this was off
          </button>
          )}
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="mp-secret-remove"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-500 duration-200 [transition-property:color] disabled:opacity-50"
          >
            <Trash2 size={12} /> Turn off
          </button>
        </div>
      )}

      {replayed && (
        <p className="mt-3 flex items-start gap-2 text-xs text-emerald-600" data-testid="mp-replay-result">
          <CheckCircle2 size={14} className="shrink-0 mt-px" />
          <span>
            {replayed.sent
              ? `${replayed.sent} earlier booking${replayed.sent === 1 ? "" : "s"} sent to GA4.`
              : "No earlier bookings were waiting — nothing was sent twice."}
          </span>
        </p>
      )}

      {error && (
        <p className="mt-3 flex items-start gap-2 text-xs text-red-600" data-testid="mp-secret-error">
          <AlertCircle size={14} className="shrink-0 mt-px" /> <span>{error}</span>
        </p>
      )}
    </div>
  );
}
